import {
  ArrowLeft, AlertTriangle, Bookmark, BookmarkCheck, ExternalLink,
  Eye, Scale, FileText, Loader2, Share2, ShieldCheck, ImageDown, Code2,
  Vote, Layers, MessageSquare, Trash2, Send, Heart,
} from "lucide-react";
import { useParams } from "react-router-dom";
import { useMemo, useState } from "react";
import { useDossier } from "@/hooks/useDossier";
import { TopicCard } from "@/components/TopicCard";
import { TelegramCTA } from "@/components/TelegramCTA";
import { scoreColor } from "@/lib/ui";

const Section = ({ icon: Icon, label, children }) => (
  <section className="border-b border-[#333] py-10">
    <div className="meta-label mb-6 flex items-center gap-2 text-[#A3A3A3]">
      <Icon className="h-4 w-4 text-[#D92D20]" strokeWidth={1.5} /> {label}
    </div>
    {children}
  </section>
);

const SideBox = ({ side, accent, tag }) => (
  <div className="panel p-6">
    <div className="font-mono text-[0.65rem] tracking-[0.2em]" style={{ color: accent }}>{tag}</div>
    <h4 className="mt-2 font-heading text-2xl text-white">{side?.label}</h4>
    <ul className="mt-4 space-y-3">
      {(side?.points || []).map((p) => (
        <li key={p} className="flex gap-3 font-body text-sm text-[#D4D4D4]">
          <span style={{ color: accent }}>▪</span> {p}
        </li>
      ))}
    </ul>
  </div>
);

const ACTION_CLS = "btn-pill";

const DossierActions = ({ saved, onBookmark, onShare, onCopyCard, onEmbed }) => (
  <div className="mt-7 flex flex-wrap gap-3">
    <button
      data-testid="bookmark-btn"
      onClick={onBookmark}
      className={`${ACTION_CLS} ${saved
        ? "border-[#FBBF24] text-[#FBBF24] hover:bg-[#FBBF24]/10"
        : "border-[#333] text-[#A3A3A3] hover:border-[#525252] hover:text-white"}`}
    >
      {saved ? <BookmarkCheck className="h-4 w-4" /> : <Bookmark className="h-4 w-4" />}
      {saved ? "Saved to archive" : "Save dossier"}
    </button>
    <button data-testid="share-btn" onClick={onShare} className={`${ACTION_CLS} border-[#333] text-[#A3A3A3] hover:border-[#D92D20]/50 hover:text-white`}>
      <Share2 className="h-4 w-4" /> Share
    </button>
    <button data-testid="copy-card-btn" onClick={onCopyCard} className={`${ACTION_CLS} border-[#333] text-[#A3A3A3] hover:border-[#FBBF24]/50 hover:text-white`}>
      <ImageDown className="h-4 w-4" /> Copy Card
    </button>
    <button data-testid="embed-btn" onClick={onEmbed} className={`${ACTION_CLS} border-[#333] text-[#A3A3A3] hover:border-[#FBBF24]/50 hover:text-white`}>
      <Code2 className="h-4 w-4" /> Embed
    </button>
  </div>
);

const DossierHeader = ({ topic, color, actions }) => (
  <div className="border-b border-[#333] pb-8">
    <div className="mb-5 flex flex-wrap items-center gap-3">
      <span className="meta-label border border-[#333] px-2 py-1 text-[#A3A3A3]">{topic.category}</span>
      <span className="flex items-center gap-1.5 font-mono text-xs" style={{ color }}>
        <AlertTriangle className="h-3.5 w-3.5" strokeWidth={1.5} /> Controversy {topic.controversy_score}/100
      </span>
      {topic.views > 0 && (
        <span className="flex items-center gap-1.5 font-mono text-xs text-[#737373]" data-testid="view-count">
          <Eye className="h-3.5 w-3.5" strokeWidth={1.5} /> {topic.views} {topic.views === 1 ? "read" : "reads"}
        </span>
      )}
    </div>
    <h1 className="font-heading text-4xl tracking-tighter text-white md:text-6xl">{topic.title}</h1>
    <p className="mt-5 font-body text-lg text-[#A3A3A3]">{topic.teaser}</p>
    <DossierActions {...actions} />
  </div>
);

const BackgroundSection = ({ paras }) => (
  <Section icon={FileText} label="01 / Background">
    {paras.map((p) => (
      <p key={p} className="mb-4 font-body leading-relaxed text-[#D4D4D4]">{p}</p>
    ))}
  </Section>
);

const TwoSidesSection = ({ a, b }) => (
  <Section icon={Scale} label="02 / The Two Sides">
    <div className="grid gap-4 md:grid-cols-2">
      <SideBox side={a} accent="#D92D20" tag="POSITION A" />
      <SideBox side={b} accent="#3B82F6" tag="POSITION B" />
    </div>
  </Section>
);

const VoteBar = ({ label, pct, count, accent, active }) => (
  <div>
    <div className="mb-1.5 flex items-center justify-between font-mono text-xs">
      <span className="text-white">{label}{active ? " · your pick" : ""}</span>
      <span style={{ color: accent }}>{pct}% · {count}</span>
    </div>
    <div className="h-2 w-full bg-[#1a1a1a]">
      <div className="h-2 transition-all duration-500" style={{ width: `${pct}%`, background: accent }} />
    </div>
  </div>
);

const VoteSection = ({ sideA, sideB, votes, votedSide, onVote }) => {
  const a = votes?.a || 0;
  const b = votes?.b || 0;
  const total = a + b;
  const pct = (n) => (total ? Math.round((n / total) * 100) : 0);
  return (
    <Section icon={Vote} label="Where do you land?">
      <div className="meta-label mb-5 text-[#A3A3A3]">Cast your read — which side do you lean?</div>
      {!votedSide ? (
        <div className="grid gap-4 md:grid-cols-2" data-testid="vote-choices">
          <button
            data-testid="vote-side-a"
            onClick={() => onVote("a")}
            className="panel p-5 text-left transition-colors hover:border-[#D92D20]/60"
          >
            <div className="font-mono text-[0.65rem] tracking-[0.2em] text-[#D92D20]">POSITION A</div>
            <div className="mt-2 font-heading text-xl text-white">{sideA?.label}</div>
          </button>
          <button
            data-testid="vote-side-b"
            onClick={() => onVote("b")}
            className="panel p-5 text-left transition-colors hover:border-[#3B82F6]/60"
          >
            <div className="font-mono text-[0.65rem] tracking-[0.2em] text-[#3B82F6]">POSITION B</div>
            <div className="mt-2 font-heading text-xl text-white">{sideB?.label}</div>
          </button>
        </div>
      ) : (
        <div className="space-y-5" data-testid="vote-results">
          <VoteBar label={sideA?.label} pct={pct(a)} count={a} accent="#D92D20" active={votedSide === "a"} />
          <VoteBar label={sideB?.label} pct={pct(b)} count={b} accent="#3B82F6" active={votedSide === "b"} />
        </div>
      )}
      <div className="meta-label mt-5 text-[#737373]">{total} {total === 1 ? "read" : "reads"} weighed in</div>
    </Section>
  );
};

const RelatedSection = ({ items }) => {
  if (!items?.length) return null;
  return (
    <Section icon={Layers} label="06 / Related Dossiers">
      <div className="grid auto-rows-[minmax(200px,auto)] gap-4 md:grid-cols-2" data-testid="related-dossiers">
        {items.map((t, i) => (
          <TopicCard key={t.id} topic={t} index={i} />
        ))}
      </div>
    </Section>
  );
};

const renderWithMentions = (text) =>
  (text || "").split(/(@\w+)/g).map((part, i) =>
    /^@\w+$/.test(part)
      ? <span key={i} className="font-mono text-[#FBBF24]" data-testid="comment-mention">{part}</span>
      : part,
  );

const CommentsSection = ({ comments, total, sort, onSort, user, onAdd, onDelete, onLike, onLoadMore }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    const ok = await onAdd(t);
    if (ok) setText("");
    setBusy(false);
  };

  const sortBtn = (key) =>
    `font-mono text-xs uppercase tracking-wider transition-colors ${
      sort === key ? "text-[#D92D20]" : "text-[#737373] hover:text-white"
    }`;

  return (
    <Section icon={MessageSquare} label={`07 / The Discussion${total ? ` · ${total}` : ""}`}>
      {user ? (
        <div className="mb-8" data-testid="comment-form">
          <textarea
            data-testid="comment-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Add your read on this controversy…"
            rows={3}
            className="w-full panel p-4 font-body text-sm text-white placeholder:text-[#737373] focus:border-[#D92D20] focus:outline-none"
          />
          <button
            data-testid="comment-submit"
            onClick={submit}
            disabled={busy || !text.trim()}
            className="btn-pill mt-3 inline-flex items-center gap-2 bg-[#D92D20] text-white hover:bg-[#b8241a] disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Post
          </button>
        </div>
      ) : (
        <p className="meta-label mb-8 text-[#737373]">Sign in to join the discussion.</p>
      )}

      {comments.length === 0 ? (
        <p className="meta-label text-[#737373]">No comments yet — be the first to weigh in.</p>
      ) : (
        <>
          <div className="mb-4 flex items-center gap-4" data-testid="comment-sort">
            <button data-testid="comment-sort-new" onClick={() => onSort("new")} className={sortBtn("new")}>Newest</button>
            <button data-testid="comment-sort-top" onClick={() => onSort("top")} className={sortBtn("top")}>Top</button>
          </div>
          <div className="space-y-4" data-testid="comment-list">
            {comments.map((c) => (
              <div key={c.id} className="panel p-4" data-testid={`comment-${c.id}`}>
                <div className="mb-2 flex items-center justify-between">
                  <span className="font-mono text-xs text-[#FBBF24]">{c.user_name}</span>
                  <div className="flex items-center gap-3">
                    <span className="meta-label text-[#737373]">{(c.created_at || "").slice(0, 10)}</span>
                    {user && (user.id === c.user_id || user.role === "admin") && (
                      <button
                        data-testid={`comment-delete-${c.id}`}
                        onClick={() => onDelete(c.id)}
                        className="text-[#737373] transition-colors hover:text-[#D92D20]"
                        aria-label="Delete comment"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>
                <p className="font-body text-sm leading-relaxed text-[#D4D4D4]">{renderWithMentions(c.text)}</p>
                <button
                  data-testid={`comment-like-${c.id}`}
                  onClick={() => onLike(c.id)}
                  className={`mt-3 inline-flex items-center gap-1.5 font-mono text-xs transition-colors ${
                    c.liked ? "text-[#D92D20]" : "text-[#737373] hover:text-[#D92D20]"
                  }`}
                >
                  <Heart className="h-3.5 w-3.5" strokeWidth={1.5} fill={c.liked ? "#D92D20" : "none"} />
                  {c.likes || 0}
                </button>
              </div>
            ))}
          </div>
          {comments.length < total && (
            <button
              data-testid="comments-load-more"
              onClick={onLoadMore}
              className="btn-pill mt-6 border border-[#333] text-[#A3A3A3] hover:border-[#D92D20]/50 hover:text-white"
            >
              Load more ({total - comments.length})
            </button>
          )}
        </>
      )}
    </Section>
  );
};

const HiddenTruthSection = ({ paras }) => (
  <Section icon={Eye} label="03 / The Hidden Truth">
    <div className="border border-[#FBBF24]/30 bg-[#FBBF24]/[0.04] p-6">
      <div className="meta-label mb-3 text-[#FBBF24]">// what the noise buries</div>
      {paras.map((p) => (
        <p key={p} className="mb-3 font-mono text-sm leading-relaxed text-[#E5E5E5]">{p}</p>
      ))}
    </div>
  </Section>
);

const KeyFactsSection = ({ facts }) => (
  <Section icon={FileText} label="04 / Key Facts">
    <ul className="space-y-3">
      {facts.map((f, i) => (
        <li key={f} className="flex gap-3 font-body text-[#D4D4D4]">
          <span className="font-mono text-[#D92D20]">{String(i + 1).padStart(2, "0")}</span>
          <span>{f}</span>
        </li>
      ))}
    </ul>
  </Section>
);

const SourcesSection = ({ sources, verifiedCount }) => (
  <Section icon={ExternalLink} label="05 / Source Links">
    {verifiedCount > 0 && (
      <div className="meta-label mb-4 flex items-center gap-2 text-[#22C55E]">
        <ShieldCheck className="h-4 w-4" strokeWidth={1.5} /> {verifiedCount} live-verified via NewsAPI
      </div>
    )}
    <div className="space-y-2">
      {sources.map((s, i) => (
        <a
          key={s.url || `${s.title}-${i}`}
          data-testid={`source-link-${i}`}
          href={s.url}
          target="_blank"
          rel="noopener noreferrer"
          className="group flex items-start justify-between gap-4 panel p-4 transition-colors hover:border-[#FBBF24]/50"
        >
          <div>
            <div className="flex items-center gap-2">
              {s.verified && <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-[#22C55E]" strokeWidth={2} />}
              <span className="font-mono text-sm text-[#FBBF24] group-hover:underline">{s.title}</span>
            </div>
            <div className="meta-label mt-1 text-[#737373]">
              {s.verified ? "VERIFIED" : "AI-CITED"} · {s.outlet} — {s.url}
            </div>
          </div>
          <ExternalLink className="mt-1 h-4 w-4 shrink-0 text-[#737373] group-hover:text-[#FBBF24]" strokeWidth={1.5} />
        </a>
      ))}
    </div>
  </Section>
);

export default function TopicDetail() {
  const { id } = useParams();
  const {
    topic, loading, error, saved, backgroundParas, hiddenTruthParas,
    toggleBookmark, shareDossier, copyCard, copyEmbed, navigate, reload,
    related, votes, votedSide, vote,
    comments, commentTotal, commentSort, changeCommentSort,
    addComment, removeComment, likeComment, loadMoreComments, user,
  } = useDossier(id);

  const actions = useMemo(
    () => ({ saved, onBookmark: toggleBookmark, onShare: shareDossier, onCopyCard: copyCard, onEmbed: copyEmbed }),
    [saved, toggleBookmark, shareDossier, copyCard, copyEmbed]
  );

  if (loading) {
    return (
      <div className="mx-auto flex max-w-3xl flex-col items-center px-6 py-32 text-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#D92D20]" />
        <p className="meta-label mt-6 text-[#A3A3A3] cursor-blink">Unsealing dossier &amp; gathering sources</p>
      </div>
    );
  }
  if (error || !topic) {
    return (
      <div className="mx-auto flex max-w-3xl flex-col items-center px-6 py-32 text-center" data-testid="dossier-error">
        <AlertTriangle className="h-10 w-10 text-[#D92D20]" strokeWidth={1.5} />
        <h1 className="mt-6 font-heading text-3xl text-white">Dossier unavailable</h1>
        <p className="meta-label mt-3 max-w-md text-[#A3A3A3]">
          We couldn't unseal this file right now. The analysis engine may be busy — try again, or head back to the archive.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <button data-testid="retry-dossier-btn" onClick={reload} className="btn-pill border-[#D92D20] bg-[#D92D20] text-white hover:bg-[#B91C1C]">
            Try again
          </button>
          <button data-testid="back-to-archive-btn" onClick={() => navigate("/")} className="btn-pill border-[#333] text-[#A3A3A3] hover:border-[#525252] hover:text-white">
            <ArrowLeft className="h-4 w-4" /> Back to archive
          </button>
        </div>
      </div>
    );
  }
  const color = scoreColor(topic.controversy_score);

  return (
    <div className="relative">
      <div
        className="pointer-events-none absolute inset-0 bg-cover bg-fixed opacity-[0.04]"
        style={{ backgroundImage: "url(https://images.unsplash.com/photo-1705837863332-7162639852d8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200)" }}
      />
      <div className="relative mx-auto max-w-4xl px-6 py-12">
        <button
          data-testid="back-btn"
          onClick={() => navigate("/")}
          className="meta-label mb-10 flex items-center gap-2 text-[#A3A3A3] transition-colors hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" /> Back to archive
        </button>

        <DossierHeader
          topic={topic}
          color={color}
          actions={actions}
        />
        <BackgroundSection paras={backgroundParas} />
        <TwoSidesSection a={topic.side_a} b={topic.side_b} />
        <VoteSection sideA={topic.side_a} sideB={topic.side_b} votes={votes} votedSide={votedSide} onVote={vote} />
        <HiddenTruthSection paras={hiddenTruthParas} />
        {topic.key_facts?.length > 0 && <KeyFactsSection facts={topic.key_facts} />}
        <SourcesSection sources={topic.sources || []} verifiedCount={topic.verified_count} />
        <RelatedSection items={related} />
        <CommentsSection
          comments={comments}
          total={commentTotal}
          sort={commentSort}
          onSort={changeCommentSort}
          user={user}
          onAdd={addComment}
          onDelete={removeComment}
          onLike={likeComment}
          onLoadMore={loadMoreComments}
        />
        <div className="mt-12 flex justify-center">
          <TelegramCTA />
        </div>
      </div>
    </div>
  );
}
